import { supabase } from '../lib/supabase';

export const projectService = {
  /**
   * Get all projects (for boss view)
   * Includes team members derived from phases.assigned_worker_id
   */
  async getAllProjects() {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) {
      console.error('Not authenticated - cannot fetch projects');
      return [];
    }

    const { data: projects, error: projectsError } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        phases (
          id,
          phase_name,
          phase_order,
          status,
          progress,
          planned_start_date,
          planned_end_date,
          assigned_worker_id,
          required_trade,
          worker:users!phases_assigned_worker_id_fkey (
            id,
            full_name,
            trade,
            avatar_url
          )
        ),
        project_alerts (
          id,
          alert_text,
          is_urgent,
          created_at
        )
      `)
      ?.order('created_at', { ascending: false });

    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
      throw projectsError;
    }

    if (!projects || projects?.length === 0) {
      return [];
    }

    return projects?.map(project => {
      const assignedWorkers = [];
      const seenWorkerIds = new Set();

      (project?.phases || [])?.forEach(phase => {
        if (phase?.worker?.id && !seenWorkerIds?.has(phase?.worker?.id)) {
          seenWorkerIds?.add(phase?.worker?.id);
          assignedWorkers?.push({
            name: phase?.worker?.full_name || '',
            role: phase?.worker?.trade || '',
            avatar: phase?.worker?.avatar_url || '',
            avatarAlt: `Professional headshot of ${phase?.worker?.full_name}`
          });
        }
      });

      return {
        id: project?.id,
        name: project?.name,
        startDate: project?.start_date,
        endDate: project?.end_date,
        status: project?.status,
        source: project?.source || 'manual',
        ai_import_id: project?.ai_import_id || null,
        lifecycle_status: project?.lifecycle_status || 'dormant',
        currentPhase: project?.current_phase,
        phaseProgress: project?.phase_progress,
        isPaid: project?.is_paid,
        teamMembers: assignedWorkers,
        phases: project?.phases || [],
        alerts: project?.project_alerts?.map(alert => alert?.alert_text) || [],
        userRole: null
      };
    });
  },

  /**
   * Get projects assigned to specific worker
   * Supports:
   * - manual projects via phases.assigned_worker_id
   * - AI imported projects via phase_tasks.assigned_worker_id
   *
   * Important:
   * WorkerHomePage should use workerPhases[].status as the source of truth.
   */
  async getWorkerProjects(userId) {
    const { data: manualAssignedPhases, error: manualError } = await supabase
      ?.from('phases')
      ?.select(`
        id,
        project_id,
        phase_name,
        phase_order,
        planned_start_date,
        planned_end_date,
        status,
        progress,
        assigned_worker_id,
        required_trade,
        storey,
        total_cost,
        source_task_name
      `)
      ?.eq('assigned_worker_id', userId);

    if (manualError) throw manualError;

    const { data: assignedTasksRaw, error: taskError } = await supabase
      ?.from('phase_tasks')
      ?.select(`
        id,
        phase_id,
        task_text,
        task_order,
        is_completed,
        assigned_worker_id,
        phase:phases (
          id,
          project_id,
          phase_name,
          phase_order,
          planned_start_date,
          planned_end_date,
          status,
          progress,
          required_trade,
          storey,
          total_cost,
          source_task_name
        )
      `)
      ?.eq('assigned_worker_id', userId);

    if (taskError) throw taskError;

    const assignedTasks = (assignedTasksRaw || [])?.map(task => {
      const normalizedPhase = Array.isArray(task?.phase)
        ? task?.phase?.[0]
        : task?.phase;

      return {
        ...task,
        phase: normalizedPhase || null
      };
    });

    const manualProjectIds = (manualAssignedPhases || [])
      ?.map(phase => phase?.project_id)
      ?.filter(Boolean);

    const aiProjectIds = assignedTasks
      ?.map(task => task?.phase?.project_id)
      ?.filter(Boolean);

    const projectIds = [
      ...new Set([
        ...(manualProjectIds || []),
        ...(aiProjectIds || [])
      ])
    ];

    if (!projectIds?.length) return [];

    const { data: projects, error: projectsError } = await supabase
      ?.from('projects')
      ?.select(`
        id,
        name,
        start_date,
        end_date,
        status,
        source,
        lifecycle_status,
        current_phase,
        phase_progress,
        is_paid,
        project_alerts (
          id,
          alert_text,
          is_urgent,
          created_at
        ),
        phases (
          id,
          phase_name,
          phase_order,
          planned_start_date,
          planned_end_date,
          status,
          progress,
          assigned_worker_id,
          required_trade,
          storey,
          total_cost,
          source_task_name
        )
      `)
      ?.in('id', projectIds);

    if (projectsError) throw projectsError;
    if (!projects || projects?.length === 0) return [];

    return projects?.map(project => {
      const allPhases = (project?.phases || [])?.sort(
        (a, b) => (a?.phase_order || 0) - (b?.phase_order || 0)
      );

      const isAiProject = project?.source === 'ai_import';

      let mappedWorkerPhases = [];
      let workerRoleForProject = null;

      if (isAiProject) {
        const myAssignedTasks = assignedTasks?.filter(
          task => task?.phase?.project_id === project?.id
        );

        const groupedByPhase = {};

        myAssignedTasks?.forEach(task => {
          const phase = task?.phase;
          if (!phase?.id) return;

          if (!groupedByPhase?.[phase?.id]) {
            groupedByPhase[phase?.id] = {
              phaseId: phase?.id,
              phaseName: phase?.phase_name,
              requiredTrade: phase?.required_trade,
              status: phase?.status,
              progress: phase?.progress || 0,
              estimatedStartDate: phase?.planned_start_date,
              estimatedEndDate: phase?.planned_end_date,
              storey: phase?.storey || null,
              totalCost: phase?.total_cost || null,
              sourceTaskName: phase?.source_task_name || null,
              tasks: []
            };
          }

          groupedByPhase[phase?.id]?.tasks?.push({
            id: task?.id,
            taskText: task?.task_text,
            taskOrder: task?.task_order,
            isCompleted: task?.is_completed
          });
        });

        mappedWorkerPhases = Object.values(groupedByPhase)?.sort((a, b) => {
          const phaseA = allPhases?.find(phase => phase?.id === a?.phaseId);
          const phaseB = allPhases?.find(phase => phase?.id === b?.phaseId);

          return (phaseA?.phase_order || 0) - (phaseB?.phase_order || 0);
        });

        workerRoleForProject = mappedWorkerPhases?.[0]?.requiredTrade || null;
      } else {
        const myPhases = allPhases?.filter(
          phase => phase?.assigned_worker_id === userId
        );

        mappedWorkerPhases = myPhases?.map(phase => ({
          phaseId: phase?.id,
          phaseName: phase?.phase_name,
          requiredTrade: phase?.required_trade,
          status: phase?.status,
          progress: phase?.progress || 0,
          estimatedStartDate: phase?.planned_start_date,
          estimatedEndDate: phase?.planned_end_date,
          storey: phase?.storey || null,
          totalCost: phase?.total_cost || null,
          sourceTaskName: phase?.source_task_name || null,
          tasks: []
        }));

        workerRoleForProject = myPhases?.[0]?.required_trade || null;
      }

      return {
        id: project?.id,
        name: project?.name,
        startDate: project?.start_date,
        endDate: project?.end_date,
        status: project?.status,
        source: project?.source || 'manual',
        lifecycle_status: project?.lifecycle_status || 'dormant',
        currentPhase: project?.current_phase,
        phaseProgress: project?.phase_progress,
        isPaid: project?.is_paid,
        teamMembers: [],
        alerts: project?.project_alerts?.map(alert => alert?.alert_text) || [],
        userRole: workerRoleForProject,
        workerRole: workerRoleForProject,
        workerPhases: mappedWorkerPhases,
        allPhases,
        remarks: []
      };
    });
  },

  /**
   * Get project details by ID
   */
  async getProjectById(projectId) {
    const { data: project, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        phases (
          *,
          worker:users!phases_assigned_worker_id_fkey (
            id,
            full_name,
            trade,
            avatar_url
          )
        ),
        project_alerts (
          id,
          alert_text,
          is_urgent,
          created_at
        )
      `)
      ?.eq('id', projectId)
      ?.single();

    if (error) throw error;
    return project;
  },

  /**
   * Update project payment status
   */
  async updatePaymentStatus(projectId, isPaid) {
    const { error } = await supabase
      ?.from('projects')
      ?.update({ is_paid: isPaid })
      ?.eq('id', projectId);

    if (error) throw error;
  },

  /**
   * Delete project and all related data
   */
  async deleteProject(projectId) {
    try {
      const { error } = await supabase
        ?.from('projects')
        ?.delete()
        ?.eq('id', projectId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  /**
   * Create new project with phases.
   * Worker assignment is done at the phase level via assigned_worker_id.
   * No project_assignments table is used.
   */
  async createProject(projectData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      if (!user) throw new Error('Not authenticated');

      const { data: project, error: projectError } = await supabase
        ?.from('projects')
        ?.insert({
          name: projectData?.name,
          start_date: projectData?.startDate,
          end_date: projectData?.endDate,
          status: projectData?.status || 'on-track',
          lifecycle_status: 'dormant',
          current_phase: projectData?.currentPhase || null,
          phase_progress: 0,
          is_paid: false,
          created_by: user?.id
        })
        ?.select()
        ?.single();

      if (projectError) throw projectError;

      const standardPhases = {
        1: 'Hacking',
        2: '3 Musketeers',
        3: 'False Ceiling',
        4: 'Tiling',
        5: 'Carpentry Measurement',
        6: 'Clearing',
        7: 'Painting',
        8: 'Carpentry Installation',
        9: '3 Musketeers Installation',
        10: 'Paint Touch-Up',
        11: 'Cleaning',
        12: 'Handover'
      };

      const phasesToInsert = [];

      for (const [phaseId, phaseName] of Object.entries(standardPhases)) {
        const phaseIdNum = parseInt(phaseId);
        const phaseData = projectData?.phases?.[phaseIdNum] || {};

        if (phaseData?.enabled !== true) continue;

        const assignedWorkerId = phaseData?.assigned_worker_id;

        if (!assignedWorkerId) {
          throw new Error(
            `No worker assigned to phase "${phaseName}". ` +
            'Please select a worker for each enabled phase before creating this project.'
          );
        }

        phasesToInsert?.push({
          project_id: project?.id,
          phase_name: phaseName,
          phase_order: phaseIdNum,
          planned_start_date: phaseData?.startDate || null,
          planned_end_date: phaseData?.endDate || null,
          assigned_worker_id: assignedWorkerId,
          status: 'pending',
          progress: 0,
          required_trade: phaseName
        });
      }

      if (phasesToInsert?.length > 0) {
        const { error: phasesError } = await supabase
          ?.from('phases')
          ?.insert(phasesToInsert);

        if (phasesError) {
          console.error('Error creating phases:', phasesError);
          throw phasesError;
        }
      }

      return project;
    } catch (error) {
      console.error('Error in createProject:', error);
      throw error;
    }
  },

  /**
   * Update project status
   */
  async updateProjectStatus(projectId, status) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.update({
        status,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', projectId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating project status:', error);
      throw new Error(error?.message || 'Failed to update project status');
    }

    return data;
  },

  /**
   * Update project lifecycle status
   */
  async updateProjectLifecycleStatus(projectId, lifecycleStatus) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.update({
        lifecycle_status: lifecycleStatus,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', projectId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating project lifecycle status:', error);
      throw new Error(error?.message || 'Failed to update project lifecycle status');
    }

    return data;
  },

  /**
   * Get all AI draft projects
   */
  async getDraftProjects() {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) throw new Error('Not authenticated');

    const { data: projects, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        phases (
          id,
          phase_name,
          phase_order,
          planned_start_date,
          planned_end_date,
          required_trade,
          assigned_worker_id,
          details_json,
          storey,
          total_cost,
          source_task_name,
          worker:users!phases_assigned_worker_id_fkey (
            id,
            full_name,
            trade
          )
        )
      `)
      ?.or('status.eq.draft,source.eq.ai_import')
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    const draftProjects = (projects || [])?.filter(project => {
      if (project?.status === 'draft') return true;

      if (
        project?.source === 'ai_import' &&
        project?.status !== 'completed' &&
        project?.status !== 'on-track' &&
        project?.status !== 'ongoing'
      ) {
        return true;
      }

      return false;
    });

    return draftProjects;
  },

  /**
   * Approve a draft project
   */
  async approveDraftProject(projectId) {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/approve-project/${projectId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to approve project');
    }

    return result;
  },

  /**
   * Reject a draft project
   */
  async rejectDraftProject(projectId) {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/reject-project/${projectId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to reject project');
    }

    return result;
  }
};
